import type {
  DeliveryChargeFulfillmentType,
  DeliveryChargeRuleListResponseType,
  OrderPaymentType,
  PriceTierDiscountListResponseType,
  ProductListResponseType,
  SupplierRelationshipFulfillmentType,
  SupplierRelationshipGetResponseType,
  PriceListVolumeDiscountsListResponseType,
} from "@sellar-io/api-v2/types";
/* oxlint-disable react/jsx-no-constructed-context-values */
import {
  calculateDeliveryOffers,
  calculateFulfillmentTypeContainerDeliveryCharge,
  calculateItemTotal,
  calculateTotal,
  calculateVatTotal,
  comparatorNameToComparator,
  CountryType,
  findBiggestApplicableVolumeDiscounts,
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
  getDiscountDescription,
  getFulfillmentTypeName,
} from "@sellar-io/common";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useDeliveryOptions } from "../../../hooks/useDeliveryOptions";
import { useDistributionDeliveryEstimate } from "../../../hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import TagManager from "react-gtm-module";
import { useLocation, useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { v4 } from "uuid";
import { Icon, OrderSummaryPrice, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { AvailableFromBadge } from "../../../components/badge/AvailableFromBadge";
import { EditDeliveryInstructionsDialog } from "../../../components/deliveryInstructions/EditDeliveryInstructionsDialog";
import { useUpdateConnectionDeliveryInstructions } from "../../../components/deliveryInstructions/useUpdateDeliveryInstructions";
import { BasicDialog } from "../../../components/modal/BasicDialog";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import {
  CUSTOMER,
  ORDER_DETAILS,
  ORDER_SUPPLIER,
} from "../../../constants/routes";
import { OrderOnBehalfOfContext } from "../../../contexts/OrderOnBehalfOfContext";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useCaseSizeRuleList } from "../../../hooks/api/v2/caseSizeRule/useCaseSizeRuleList";
import { useCheckoutSupplierPaymentOptions } from "../../../hooks/api/v2/checkout/useCheckoutSupplierPaymentOptions";
import { useDeliveryChargeRuleList } from "../../../hooks/api/v2/deliveryChargeRule/useDeliveryChargeRuleList";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useOrderCreate } from "../../../hooks/api/v2/order/v2/useOrderCreate";
import { usePriceListDiscountList } from "../../../hooks/api/v2/priceListDiscount/usePriceListDiscountList";
import { usePriceListVolumeDiscountsList } from "../../../hooks/api/v2/priceListVolumeDiscounts/usePriceListVolumeDiscountsList";
import { useSupplierRelationship } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationship";
import { useIsSellarAdmin } from "../../../hooks/useIsSellarAdmin";
import { useFocusedNavigation } from "../../../hooks/useFocusedNavigation";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { renderTaxName } from "../../../utils/sellarHelpers";
import { OrderRefundedAlert } from "../Alerts/OrderRefundedAlert";
import { DeliveryCard } from "./cards/DeliveryCard";
import { AddToOrderDialog } from "./dialogs/AddToOrderDialog";
import { CustomOrderDiscountDialog } from "./dialogs/CustomOrderDiscountDialog";
import { EditDeliveryAddressDialog } from "./dialogs/EditDeliveryAddressDialog";
import { EditOrderItemDialog } from "./dialogs/EditOrderItemDialog";
import { FulfillmentTypeDialog } from "./dialogs/FulfillmentTypeDialog";
import { calculateCustomDiscountAmount } from "./helpers/calculateDiscountAmount";
import { SupplierPaymentTypeRadioGroup } from "./components/SupplierPaymentTypeRadioGroup";
import { ChatInput } from "../../../components/chatInput/ChatInput";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H6,
  InputLabel,
  InputContainer,
  InputText,
  InputTel,
} from "@sellar-io/components";
import { useFulfillmentTypeGetV2 } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeGetV2";
import {
  asOrderItemDeliveryDetails,
  doesOrderQualifyForDeliveryChargeRule,
} from "./OrderOnBehalfOf.shared";

dayjs.extend(advancedFormat);

export type OrderOnBehalfOfOrderItemType = {
  quantity: number;
  price: number;
  fullPrice: number;
  Product: ProductListResponseType[number];
  discountable: boolean;
};

export interface OrderDiscountType extends Omit<
  PriceTierDiscountListResponseType[number],
  "id" | "PriceTierId" | "type"
> {
  type: "percentage" | "perpetual-flat";
}

export type CustomDiscountType = {
  type: "percentage" | "flat";
  amount: number;
};

export type DeliveryAddressType = {
  name: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: CountryType;
};

export const OrderOnBehalfOf = () => {
  const connectionId = useParamAsNumber(ORDER_SUPPLIER, "connectionId");
  const { data: connection } = useSupplierRelationship(connectionId);
  if (!connection) {
    return <SpinnerLegacy />;
  }
  if (connection.PriceTier) {
    return (
      <OrderWithVolumeDiscounts
        connectionId={connectionId}
        connection={connection}
        priceListId={connection.PriceTier.id}
      />
    );
  }
  return (
    <OrderOnBehalfOfContent
      connectionId={connectionId}
      connection={connection}
    />
  );
};

type OrderContentProps = {
  connectionId: number;
  connection: SupplierRelationshipGetResponseType;
  volumeDiscountAssignments?: PriceListVolumeDiscountsListResponseType;
};

const OrderWithVolumeDiscounts = ({
  priceListId,
  ...props
}: Omit<OrderContentProps, "volumeDiscountAssignments"> & {
  priceListId: number;
}) => {
  const { data } = usePriceListVolumeDiscountsList(priceListId);
  return <OrderOnBehalfOfContent {...props} volumeDiscountAssignments={data} />;
};

const OrderOnBehalfOfContent = ({
  connectionId,
  connection,
  volumeDiscountAssignments,
}: OrderContentProps) => {
  const { isActive } = useRemoteFeatureFlags();
  const isOoboSellarCreditActive = isActive("sellarCreditOobo");
  const isCheckoutPaymentLinkRequestActive = isActive(
    "checkoutPaymentLinkRequest",
  );
  const unbundlingEnabled = isActive("unbundlingReleaseFinal");
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsSellarAdmin();

  // Sellar Distribution contact stuff
  const [contactName, setContactName] = useState<string>("");
  const [contactEmail, setContactEmail] = useState<string>();
  const [contactPhoneNumber, setContactPhoneNumber] = useState<string>();
  const [timePreference, setTimePreference] = useState<
    "am" | "pm" | "allDay" | null
  >(null);

  const retailerBusinessDeliveryInstructions =
    connection?.Retailer.Business.deliveryInstructions;
  const buyerBusinessName = connection?.Retailer.Business.name;

  const [previousContactConnectionId, setPreviousContactConnectionId] =
    useState<number>();
  if (connection && connection.id !== previousContactConnectionId) {
    setPreviousContactConnectionId(connection.id);
    if (!contactEmail) {
      setContactEmail(connection.buyerContactEmail ?? "");
    }
    if (!contactPhoneNumber)
      setContactPhoneNumber(connection.retailerContactSmsNumber || "");
  }

  const caseSizeRuleListQuery = useCaseSizeRuleList({
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const [fulfillmentType, setFulfillmentType] =
    useState<SupplierRelationshipFulfillmentType>("direct");

  const priceTierDiscountListQuery = usePriceListDiscountList(
    {
      query: [
        {
          type: "VolumeDiscountsId",
          modifier: "EQUALS",
          value: connection?.PriceTier?.VolumeDiscountsId ?? 0,
        },
      ],
    },
    { enabled: !!connection?.PriceTier?.VolumeDiscountsId },
  );

  const applicablePriceListDiscounts = useMemo(() => {
    const assignments = volumeDiscountAssignments ?? [];
    const discountFulfillmentType =
      fulfillmentType === "collection" ? null : fulfillmentType;
    const fulfillmentSpecificAssignments = assignments.filter(
      (assignment) => assignment.fulfillmentType === discountFulfillmentType,
    );
    const fallbackAssignments = assignments.filter(
      (assignment) => assignment.fulfillmentType === null,
    );
    const applicableAssignments = fulfillmentSpecificAssignments.length
      ? fulfillmentSpecificAssignments
      : fallbackAssignments;
    const assignedDiscounts = applicableAssignments.flatMap((assignment) =>
      assignment.VolumeDiscounts.PriceListDiscounts.map((discount) => ({
        ...discount,
        deletedAt: null,
        VolumeDiscountsId: assignment.VolumeDiscountsId,
      })),
    );

    if (assignedDiscounts.length) return assignedDiscounts;

    return priceTierDiscountListQuery.data?.pages[0] ?? [];
  }, [
    fulfillmentType,
    priceTierDiscountListQuery.data,
    volumeDiscountAssignments,
  ]);

  const orderCreateMutation = useOrderCreate();
  useFocusedNavigation();

  //This is needed to keep track of all the loading changes of orderCreation, there could a few different loading states
  const [orderCreationLoading, setOrderCreationLoading] = useState(false);

  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddressType>();

  const [orderItems, setOrderItems] = useState<OrderOnBehalfOfOrderItemType[]>(
    () =>
      (location.state?.initialOrderItems as
        | OrderOnBehalfOfOrderItemType[]
        | undefined) ?? [],
  );

  const {
    distributionEnabled,
    isBuyerInDistributionArea,
    isFetched: deliveryOptionsFetched,
  } = useDeliveryOptions({
    postcode: deliveryAddress?.postalCode,
    includeShadowRoutes: true,
  });
  const distributionAvailable =
    distributionEnabled && isBuyerInDistributionArea;
  const { nextDeliveryDate, orderByDate, isFetching } =
    useDistributionDeliveryEstimate({
      supplierId: business?.Supplier?.id ?? 0,
      // Only future preorders can change the delivery estimate.
      productIds: orderItems
        .filter(
          (item) =>
            findLatestPreOrderAvailableFromDate([item.Product]) !== null,
        )
        .map((item) => item.Product.id)
        .sort((a, b) => a - b),
      buyerPostcode: deliveryAddress?.postalCode ?? "",
      cutoffPolicy: "supplierOrderFinalisation",
      enabled: distributionAvailable,
    });
  let distributionDeliveryEstimate =
    distributionAvailable &&
    nextDeliveryDate &&
    dayjs(nextDeliveryDate).isValid()
      ? `For delivery by ${dayjs(nextDeliveryDate).tz("Europe/London").format("ddd Do MMM")}`
      : undefined;
  let distributionOrderBy =
    distributionDeliveryEstimate && orderByDate && dayjs(orderByDate).isValid()
      ? `Finalise by ${dayjs(orderByDate).tz("Europe/London").format("h:mma, ddd Do MMM")}`
      : undefined;

  if (distributionEnabled && (!deliveryOptionsFetched || isFetching)) {
    distributionDeliveryEstimate = "Loading delivery date…";
    distributionOrderBy = "Loading finalisation deadline…";
  }

  const [idempotencyKey, setIdempotencyKey] = useState<string>(v4());
  const [errorMessage, setErrorMessage] = useState<string>();

  const [customDeliveryCharge, setCustomDeliveryCharge] = useState<
    number | undefined
  >(undefined);

  const [paymentType, setPaymentType] = useState<OrderPaymentType>();
  const [paymentLinkRequested, setPaymentLinkRequested] = useState(false);
  const paymentOptionsQuery = useCheckoutSupplierPaymentOptions({
    connectionId,
  });

  const availablePaymentTypes = paymentOptionsQuery.data?.paymentTypes ?? [];

  const defaultPaymentType =
    isCheckoutPaymentLinkRequestActive &&
    !availablePaymentTypes.includes("sellarCredit") &&
    (availablePaymentTypes.includes("manual") ||
      availablePaymentTypes.includes("stripe"))
      ? "manual"
      : undefined;

  const selectedPaymentType = paymentType ?? defaultPaymentType;

  const fulfillmentTypeQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "FulfillmentType",
          value: ["direct", "haulier", "sellarDistribution"].includes(
            fulfillmentType,
          )
            ? (fulfillmentType as DeliveryChargeFulfillmentType)
            : "courier",
          modifier: "EQUALS",
        },
      ],
      sortBy: "createdAt",
      sortDir: "desc",
      limit: 1,
    },
    {
      enabled: ["courier", "direct", "ups", "haulier"].includes(
        fulfillmentType,
      ),
    },
  );

  const { data: deliveryMethod } = useFulfillmentTypeGetV2(
    {
      query: [
        {
          type: "RetailerId",
          value: connection?.BuyerId ?? 0,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: ["direct", "haulier", "sellarDistribution"].includes(
            fulfillmentType,
          )
            ? (fulfillmentType as DeliveryChargeFulfillmentType)
            : "courier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled:
        ["courier", "direct", "haulier"].includes(fulfillmentType) &&
        unbundlingEnabled &&
        Boolean(connection?.BuyerId),
    },
  );

  const deliveryChargeRuleListQuery = useDeliveryChargeRuleList({
    query: [
      {
        type: "col",
        modifier: "EQUALS",
        value: "subtotal",
      },
    ],
  });

  const [customDiscount, setCustomDiscount] = useState<CustomDiscountType>();

  const [editingProductId, setEditingProductId] = useState<number>();

  const [orderItemDialogActive, setOrderItemDialogActive] = useState(false);
  const [fulfillmentTypeDialogActive, setFulfillmentTypeDialogActive] =
    useState(false);
  const [editDeliveryInstructionsActive, setEditDeliveryInstructionsActive] =
    useState<boolean>(false);
  const {
    updateConnectionDeliveryInstructions,
    isLoading: updatingDeliveryInstruction,
  } = useUpdateConnectionDeliveryInstructions(connectionId);

  const [discardOrderDialogActive, setDiscardOrderDialogActive] =
    useState(false);
  const [discountDialogActive, setDiscountDialogActive] = useState(false);
  const [paymentTypeAlertActive, setPaymentTypeAlertActive] = useState(false);

  const [deliveryInformationDialogActive, setDeliveryInformationDialogActive] =
    useState(false);

  const [deliveryAddressDialogActive, setDeliveryAddressDialogActive] =
    useState(false);

  const [message, setMessage] = useState<string>();
  const refundAmount = location.state?.initialRefundAmount ?? 0;

  const [previousConnectionId, setPreviousConnectionId] = useState<number>();
  if (connection && connection.id !== previousConnectionId) {
    setPreviousConnectionId(connection.id);
    setFulfillmentType(connection.fulfillmentType);
    setDeliveryAddress(connection.Retailer.Business.DeliveryAddress);
  }

  const priceTierDiscountList =
    volumeDiscountAssignments || priceTierDiscountListQuery.data
      ? findBiggestApplicableVolumeDiscounts({
          volumeDiscountList: applicablePriceListDiscounts,
          caseSizeRuleList: caseSizeRuleListQuery.data?.pages[0] ?? [],
          orderItemList: orderItems.map((orderItem) => ({
            ...orderItem,
            packQuantity: orderItem.Product.packQuantity ?? null,
            volume: orderItem.Product.volume ?? null,
            unit: orderItem.Product.unit ?? null,
            containerType: orderItem.Product.containerType ?? null,
          })),
        })
      : [];
  const customDiscountAmount = customDiscount
    ? calculateCustomDiscountAmount({
        totalValue: calculateItemTotal(orderItems),
        customDiscount,
      })
    : undefined;
  const priceTierDiscountAmount = priceTierDiscountList.reduce(
    (total, discount) => total + discount.discountAmount,
    0,
  );

  if (!business?.Supplier) throw new Error("Must be supplier");

  const navigateBack = () =>
    navigate(CUSTOMER.href({ connectionId: connectionId }));

  const orderItemsAsDeliveryDetails = orderItems.map((orderItem) =>
    asOrderItemDeliveryDetails(orderItem),
  );

  let deliveryChargeRule:
    | Pick<
        DeliveryChargeRuleListResponseType[number],
        "comparator" | "value" | "deliveryCharge"
      >
    | undefined = deliveryChargeRuleListQuery.data?.pages[0]?.find(
    (rule) => rule.fulfillmentType === fulfillmentType,
  );

  let flatDeliveryCharge =
    fulfillmentTypeQuery.data?.pages[0]?.[0]?.charge ?? 0;

  let deliveryChargeCap =
    fulfillmentTypeQuery.data?.pages[0]?.[0]?.deliveryChargeCap;

  let fulfillmentTypeContainerDeliveryCharges =
    fulfillmentTypeQuery.data?.pages[0]?.[0]
      ?.FulfillmentTypeContainerDeliveryCharges ?? [];

  let containerTypeDeliveryChargeRules =
    fulfillmentTypeQuery.data?.pages[0]?.[0]
      ?.ContainerTypeDeliveryChargeRules ?? [];

  let freeDeliveryBasketSubtotal =
    fulfillmentTypeQuery.data?.pages[0]?.[0]?.freeDeliveryBasketSubtotal ??
    null;

  if (unbundlingEnabled && deliveryMethod) {
    deliveryChargeRule = deliveryMethod.freeDeliveryBasketSubtotal
      ? {
          comparator: "greaterThanEqual",
          value: deliveryMethod.freeDeliveryBasketSubtotal,
          deliveryCharge: 0,
        }
      : undefined;

    flatDeliveryCharge = deliveryMethod.charge ?? 0;

    deliveryChargeCap = deliveryMethod.deliveryChargeCap;

    freeDeliveryBasketSubtotal =
      deliveryMethod.freeDeliveryBasketSubtotal ?? null;

    fulfillmentTypeContainerDeliveryCharges =
      deliveryMethod.FulfillmentTypeContainerDeliveryCharges;

    containerTypeDeliveryChargeRules =
      deliveryMethod.ContainerTypeDeliveryChargeRules;
  }

  const orderQualifiesDeliveryChargeRule =
    !!deliveryChargeRule &&
    doesOrderQualifyForDeliveryChargeRule(deliveryChargeRule, orderItems);

  const calculatedContainerTypeDeliveryOffers = calculateDeliveryOffers(
    containerTypeDeliveryChargeRules,
    orderItemsAsDeliveryDetails,
  );

  const calculatedDeliveryCharge =
    calculateFulfillmentTypeContainerDeliveryCharge({
      orderItems: orderItems.map((orderItem) =>
        asOrderItemDeliveryDetails(orderItem),
      ),
      deliveryChargeRules: deliveryChargeRule
        ? [
            {
              ...deliveryChargeRule,
              comparator: comparatorNameToComparator(
                deliveryChargeRule.comparator,
              ),
            },
          ]
        : [],
      fulfillmentTypeContainerDeliveryCharges:
        fulfillmentTypeContainerDeliveryCharges,
      flatDeliveryCharge,
      calculatedContainerTypeDeliveryOffers,
      deliveryChargeCap,
      freeDeliveryBasketSubtotal,
    });

  const deliveryCharge =
    customDeliveryCharge ?? calculatedDeliveryCharge.charge;
  const discountAmount = customDiscountAmount ?? priceTierDiscountAmount;

  const handleOrder = async (parameters?: { saveAsRequest: boolean }) => {
    setErrorMessage("");
    if (isOoboSellarCreditActive && !selectedPaymentType) {
      return setPaymentTypeAlertActive(true);
    }

    setOrderCreationLoading(true);

    try {
      if (!connection) throw new Error("Cannot find connection");
      if (!fulfillmentType)
        throw new Error("Fulfillment type should be defined");
      if (!deliveryAddress) throw new Error("Cannot find delivery address");

      const usePaymentLinkFromStripe =
        isCheckoutPaymentLinkRequestActive && selectedPaymentType === "stripe";
      const usePaymentLinkFromSwitch =
        isCheckoutPaymentLinkRequestActive &&
        selectedPaymentType === "manual" &&
        paymentLinkRequested;
      const shouldRequestPaymentLink =
        usePaymentLinkFromStripe || usePaymentLinkFromSwitch;
      await orderCreateMutation.mutateAsync(
        {
          data: {
            initiator: "supplier",
            fulfillmentType,
            RetailerId: connection.BuyerId ?? 0,
            InvoiceAddressId: connection.Retailer.Business.InvoiceAddressId,
            deliveryAddress: deliveryAddress,
            deliveryCharge,
            discountAmount,
            OrderItems: orderItems.map((orderItem) => ({
              ProductId: Number(orderItem.Product.id),
              quantity: orderItem.quantity,
              price: orderItem.price,
            })),
            orderComment: message ? message.trim() : null,
            hasNoDraughtDutyRelief: false, // this cannot be set when creating an OOBO, but can be set when finalizing the order
            saveAsRequest: parameters?.saveAsRequest,
            paymentType: usePaymentLinkFromStripe
              ? "manual"
              : selectedPaymentType || null,
            ...(shouldRequestPaymentLink ? { paymentLinkRequested: true } : {}),
            ...(fulfillmentType === "sellarDistribution"
              ? {
                  distributionContactName: contactName,
                  distributionContactEmail: contactEmail,
                  distributionContactPhoneNumber: contactPhoneNumber,
                  RetailerSellarDistributionMeta: {
                    timePreference,
                  },
                }
              : {}),
          },
          idempotencyKey,
        },
        {
          onSuccess: (data) => {
            TagManager.dataLayer({
              dataLayer: {
                event: "order_created",
                orderId: data.id,
                businessType: "supplier",
                orderType: "single",
              },
            });

            const tab =
              selectedPaymentType === "sellarCredit"
                ? "Order+requests"
                : "All+orders";
            navigate(`${ORDER_DETAILS.href({ orderId: data.id })}?tab=${tab}`);
          },
          onError: (error) => {
            const errorMessage =
              apiV2AssertError(error)?.response?.data.message;
            if (errorMessage === "The idempotency key has expired.") {
              setErrorMessage(
                "Your request has timed out, please try placing your order again.",
              );
              return setIdempotencyKey(v4());
            }

            if (
              errorMessage ===
              "Cannot use Sellar Credit for an order total of £0. Change the payment method and try again."
            ) {
              setErrorMessage(errorMessage);
              setIdempotencyKey(v4());
              return;
            }
            setErrorMessage(errorMessage);
          },
        },
      );
    } finally {
      setOrderCreationLoading(false);
    }
  };

  const getActionButtons = () => {
    if (selectedPaymentType === "sellarCredit" || !isAdmin) {
      return (
        <div className={"flex w-full gap-2"}>
          <Button className="w-full" onClick={() => handleOrder()}>
            Confirm
          </Button>
        </div>
      );
    }
    return (
      <div className={"flex w-full gap-2"}>
        <Button
          className="w-full"
          color={"secondary"}
          onClick={() => handleOrder()}
        >
          Save order
        </Button>
        {isAdmin && (
          <Button
            className="w-full"
            onClick={() => handleOrder({ saveAsRequest: true })}
          >
            Save as request
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <NavigationBar
        title={"Create Order"}
        leftIcon={"CLOSE"}
        onClickLeftIcon={() => {
          return orderItems.length
            ? setDiscardOrderDialogActive(true)
            : navigateBack();
        }}
      />
      {!!refundAmount && (
        <div className={"flex justify-center"}>
          <OrderRefundedAlert
            className={"m-4 w-5/6"}
            refundAmount={refundAmount}
          />
        </div>
      )}
      {orderCreationLoading ? (
        <SpinnerLegacy />
      ) : !connection ? (
        <div>Connection not found</div>
      ) : (
        <OrderOnBehalfOfContext.Provider
          value={{
            orderItems,
            setOrderItems,
            editingProductId,
            setEditingProductId,
            fulfillmentType,
            calculatedDeliveryCharge: calculatedDeliveryCharge.charge,
            customDeliveryCharge,
            customDiscount,
            customDiscountAmount,
            priceTierDiscountList: priceTierDiscountList.map(
              (discount) => discount.volumeDiscount,
            ),
            priceTierDiscountAmount,
            connection: connection,
            fulfillmentTypeContainerDeliveryCharges,
          }}
        >
          <div className={"m-auto flex max-w-[900px] flex-col gap-4 p-4"}>
            {errorMessage && (
              <Alert
                type={"error"}
                className={"w-full"}
                message={errorMessage}
              />
            )}
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex cursor-pointer justify-between p-4",
                )}
                onClick={() => setDeliveryAddressDialogActive(true)}
              >
                <div>
                  <H3>{connection.Retailer.Business.name}</H3>
                  <H6>
                    {[
                      deliveryAddress?.name,
                      deliveryAddress?.street,
                      deliveryAddress?.city,
                      deliveryAddress?.region,
                      deliveryAddress?.postalCode,
                      deliveryAddress?.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </H6>
                </div>
                <Icon icon={"PENCIL"} />
              </CardHeader>
              <EditDeliveryAddressDialog
                address={deliveryAddress}
                active={deliveryAddressDialogActive}
                onDismiss={() => setDeliveryAddressDialogActive(false)}
                onSubmit={(data) => {
                  setDeliveryAddress(data);
                  setDeliveryAddressDialogActive(false);
                }}
              />
              <CardContent
                className={"flex cursor-pointer justify-between p-4"}
                onClick={() => setFulfillmentTypeDialogActive(true)}
              >
                <div>
                  <div>Fulfillment type</div>
                  <H6>{getFulfillmentTypeName(fulfillmentType)}</H6>
                  {fulfillmentType === "sellarDistribution" && (
                    <div className="mt-1 text-sm text-zinc-600">
                      {distributionOrderBy && <p>{distributionOrderBy}</p>}
                      <p className="font-bold">
                        {distributionDeliveryEstimate ??
                          "Delivery date unavailable"}
                      </p>
                    </div>
                  )}
                </div>
                <Icon icon={"PENCIL"} />
              </CardContent>
              <FulfillmentTypeDialog
                distributionDeliveryEstimate={distributionDeliveryEstimate}
                distributionOrderBy={distributionOrderBy}
                fulfillmentType={fulfillmentType}
                active={fulfillmentTypeDialogActive}
                onDismiss={() => setFulfillmentTypeDialogActive(false)}
                onSubmit={(fulfillmentType) => {
                  setFulfillmentType(fulfillmentType ?? "courier");
                  setFulfillmentTypeDialogActive(false);
                }}
                postcode={deliveryAddress?.postalCode}
              />

              {buyerBusinessName &&
              retailerBusinessDeliveryInstructions?.length ? (
                <CardContent className={"flex justify-between p-4"}>
                  <div className="flex items-center">
                    <Icon icon={"NOTE_TEXT_OUTLINE"} />
                    <div className="text-zinc-700">
                      <div>{buyerBusinessName}:</div>
                      <div className="text-zinc-500 italic">
                        {retailerBusinessDeliveryInstructions}
                      </div>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardContent
                    className={"flex cursor-pointer justify-between p-4"}
                    onClick={() => setEditDeliveryInstructionsActive(true)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex shrink-0">
                        <Icon icon={"NOTE_TEXT_OUTLINE"} />
                      </div>
                      <div className="text-zinc-700">
                        {connection.deliveryInstructions ??
                          "No delivery instructions provided"}
                      </div>
                    </div>
                    <Icon icon={"PENCIL"} />
                  </CardContent>
                  <EditDeliveryInstructionsDialog
                    initialDeliveryInstructions={
                      connection.deliveryInstructions ?? ""
                    }
                    active={editDeliveryInstructionsActive}
                    onDismiss={() => setEditDeliveryInstructionsActive(false)}
                    onSubmit={(data) => {
                      updateConnectionDeliveryInstructions({
                        data,
                        onSuccess: () => {
                          setEditDeliveryInstructionsActive(false);
                        },
                      });
                    }}
                    loading={updatingDeliveryInstruction}
                  />
                </>
              )}
            </Card>

            <Card className="bg-white">
              <CardHeader className={twMerge("bg-white", "p-4")}>
                <H3>Line items</H3>
              </CardHeader>
              <CardContent className={"divide-y p-0 *:p-4"}>
                {orderItems.map((orderItem) => (
                  <div
                    key={orderItem.Product.id}
                    className={"flex cursor-pointer justify-between"}
                    onClick={() => setEditingProductId(orderItem.Product.id)}
                  >
                    <div className="flex flex-col gap-1">
                      <span>{orderItem.Product.name?.split(" - ")[0]}</span>
                      <H6>
                        {orderItem.Product.name
                          ?.split(" - ")
                          .slice(1)
                          .join(" - ")}
                      </H6>
                      {!!orderItem.Product.availableFrom && (
                        <AvailableFromBadge
                          availableFrom={orderItem.Product.availableFrom}
                        />
                      )}
                    </div>
                    <div>
                      <div className={"flex items-center gap-3 self-start"}>
                        <span>{orderItem.quantity}x</span>{" "}
                        <span>{formatCurrency(orderItem.price)}</span>
                        <Icon icon={"PENCIL"} />
                      </div>
                      {!orderItem.discountable && <H6>Not discountable</H6>}
                    </div>
                    <EditOrderItemDialog
                      key={orderItem.Product.id}
                      orderItem={orderItem}
                      onUpdate={(updatedOrderItem) => {
                        setOrderItems(
                          orderItems.map((orderItem) => {
                            if (
                              orderItem.Product.id ===
                              updatedOrderItem.Product.id
                            ) {
                              return updatedOrderItem;
                            }
                            return orderItem;
                          }),
                        );
                        setEditingProductId(undefined);
                      }}
                      onDelete={() => {
                        setEditingProductId(undefined);
                        setTimeout(
                          () =>
                            setOrderItems(
                              orderItems.filter(
                                (existingOrderItem) =>
                                  existingOrderItem.Product.id !==
                                  orderItem.Product.id,
                              ),
                            ),
                          200,
                        );
                      }}
                      onDismiss={() => {
                        setEditingProductId(undefined);
                      }}
                    />
                  </div>
                ))}
                <div
                  className={"flex cursor-pointer items-center gap-1"}
                  onClick={() => setOrderItemDialogActive(true)}
                >
                  <Icon icon={"PLUS_CIRCLE"} /> Add line item
                </div>
                <AddToOrderDialog
                  active={orderItemDialogActive}
                  onDismiss={() => setOrderItemDialogActive(false)}
                  onSubmit={(newOrderItem) => {
                    setOrderItems([...orderItems, newOrderItem]);
                    setOrderItemDialogActive(false);
                  }}
                />
              </CardContent>
            </Card>
            {orderItems.length ? (
              <>
                <Card className="bg-white">
                  <CardHeader
                    className={twMerge(
                      "bg-white",
                      "flex items-center justify-between p-4",
                    )}
                  >
                    <H3>Discounts</H3>
                    <Button
                      size={"small"}
                      color={"transparent"}
                      onClick={() => setDeliveryInformationDialogActive(true)}
                    >
                      <Icon icon={"INFORMATION_OUTLINE"} />
                    </Button>
                    <BasicDialog
                      dialogActive={deliveryInformationDialogActive}
                      onDismiss={() =>
                        setDeliveryInformationDialogActive(false)
                      }
                      dismissButtonText="dismiss"
                      title={"About discounts"}
                      description="Any volume discount will be applied automatically. You can override these discounts by setting you own custom discount."
                    />
                  </CardHeader>
                  <CardContent className={"divide-y p-0 *:p-4"}>
                    {priceTierDiscountList && (
                      <div>
                        <div>Volume discounts</div>
                        {priceTierDiscountList.map((discount) => {
                          return (
                            <div
                              className={twMerge(
                                "flex justify-between",
                                customDiscount && "line-through opacity-60",
                              )}
                              key={discount.volumeDiscount.itemType}
                            >
                              <div>
                                <H6>
                                  {getDiscountDescription({
                                    discount: discount.volumeDiscount,
                                  })}
                                </H6>
                              </div>
                              <div>
                                {formatCurrency(discount.discountAmount)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {customDiscount &&
                    typeof customDiscountAmount === "number" ? (
                      <div
                        className={"flex cursor-pointer justify-between"}
                        onClick={() => setDiscountDialogActive(true)}
                      >
                        <div>
                          <div>Custom discount</div>
                          <H6>
                            {customDiscount.type === "percentage"
                              ? `Percentage (${customDiscount.amount}%)`
                              : `Fixed amount (${formatCurrency(
                                  customDiscount.amount,
                                )})`}
                          </H6>
                        </div>
                        <div className={"flex items-center gap-2 self-start"}>
                          {formatCurrency(-customDiscountAmount)}
                          <Icon className={"text-primary"} icon={"PENCIL"} />
                        </div>
                      </div>
                    ) : (
                      <div
                        className={"flex cursor-pointer items-center gap-1"}
                        onClick={() => setDiscountDialogActive(true)}
                      >
                        <Icon icon={"PLUS_CIRCLE"} /> Add custom discount
                      </div>
                    )}
                    <CustomOrderDiscountDialog
                      active={discountDialogActive}
                      onDismiss={() => setDiscountDialogActive(false)}
                      onSubmit={(discount) => {
                        setDiscountDialogActive(false);
                        setTimeout(() => {
                          setCustomDiscount(discount);
                        }, 200);
                      }}
                      onDelete={() => {
                        setDiscountDialogActive(false);
                        setTimeout(() => {
                          setCustomDiscount(undefined);
                        }, 200);
                      }}
                    />
                  </CardContent>
                </Card>

                <DeliveryCard
                  customDeliveryCharge={customDeliveryCharge}
                  deliveryChargeRule={deliveryChargeRule}
                  setCustomDeliveryCharge={setCustomDeliveryCharge}
                  deliveryCharge={calculatedDeliveryCharge}
                  calculatedContainerTypeDeliveryOffers={
                    calculatedContainerTypeDeliveryOffers
                  }
                  flatDeliveryCharge={flatDeliveryCharge}
                  orderQualifiesDeliveryChargeRule={
                    orderQualifiesDeliveryChargeRule
                  }
                />

                <Card className="bg-white">
                  <CardHeader
                    className={twMerge("bg-white", "flex flex-col gap-4 p-4")}
                  >
                    <H3>Order summary</H3>
                  </CardHeader>
                  <CardContent className={"p-4"}>
                    <OrderSummaryPrice
                      delivery={formatCurrency(deliveryCharge)}
                      subTotal={formatCurrency(calculateItemTotal(orderItems))}
                      discount={
                        discountAmount > 0
                          ? {
                              calculatedTotal: formatCurrency(discountAmount),
                              value: formatCurrency(discountAmount),
                            }
                          : undefined
                      }
                      tax={{
                        name: renderTaxName("gbp"),
                        percentage:
                          connection.vatPercentage ||
                          connection.vatPercentage === 0
                            ? connection.vatPercentage.toString()
                            : connection.Supplier.vatPercentage.toString(),
                        total: formatCurrency(
                          calculateVatTotal({
                            orderItems,
                            deliveryCharge,
                            discountAmount,
                            vatPercentage:
                              connection.vatPercentage ||
                              connection.vatPercentage === 0
                                ? connection.vatPercentage
                                : connection.Supplier.vatPercentage,
                          }),
                        ),
                      }}
                      total={formatCurrency(
                        calculateTotal({
                          orderItems,
                          deliveryCharge,
                          discountAmount,
                          vatPercentage:
                            connection.vatPercentage ||
                            connection.vatPercentage === 0
                              ? connection.vatPercentage
                              : connection.Supplier.vatPercentage,
                        }),
                      )}
                    />
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardHeader
                    className={twMerge("bg-white", "flex flex-col gap-4 p-4")}
                  >
                    <H3>Payment method</H3>
                  </CardHeader>
                  <CardContent className={"p-4"}>
                    <SupplierPaymentTypeRadioGroup
                      paymentTypes={availablePaymentTypes}
                      isLoading={paymentOptionsQuery.isLoading}
                      paymentType={selectedPaymentType}
                      onChange={(value) => {
                        setPaymentType(value);
                        setPaymentTypeAlertActive(false);
                      }}
                      paymentTypeAlertActive={paymentTypeAlertActive}
                      paymentLinkRequested={paymentLinkRequested}
                      onChangePaymentLinkRequested={setPaymentLinkRequested}
                    />
                  </CardContent>
                </Card>

                <Card className={twMerge("bg-white", "p-0")}>
                  <CardHeader className={twMerge("bg-white", "p-4")}>
                    <H3>Add a comment</H3>
                  </CardHeader>
                  <CardContent className={"flex flex-col gap-4 p-4"}>
                    <ChatInput
                      value={message ?? ""}
                      setValue={(value) => setMessage(value)}
                      placeholder={`Let ${connection.Retailer.Business.name} know any other details about their order...`}
                    />
                  </CardContent>
                </Card>
                {fulfillmentType === "sellarDistribution" && (
                  <Card className="bg-white">
                    <CardHeader className={twMerge("bg-white", "p-4")}>
                      <H3>Add contact details for Sellar Distribution</H3>
                    </CardHeader>
                    <CardContent className={"flex flex-col gap-4 p-4"}>
                      <div className="flex flex-col gap-1">
                        <InputLabel>Delivery contact</InputLabel>
                        <p className="text-zinc-500">
                          Enter the contact details for the person receiving the
                          delivery (if you know them)
                        </p>
                        <div className="flex w-full flex-col gap-4">
                          <div className="flex w-full flex-col gap-2 md:flex-row!">
                            <div className="flex w-full flex-col gap-1">
                              <InputLabel>*Name</InputLabel>
                              <InputContainer>
                                <InputText
                                  value={contactName}
                                  onChange={(e) =>
                                    setContactName(e.target.value)
                                  }
                                />
                              </InputContainer>
                            </div>

                            <div className="flex w-full flex-col gap-1">
                              <InputLabel>*Phone number</InputLabel>
                              <InputContainer>
                                <InputTel
                                  value={contactPhoneNumber}
                                  onChange={(e) =>
                                    setContactPhoneNumber(e.target.value)
                                  }
                                />
                              </InputContainer>
                            </div>

                            <div className="flex w-full flex-col gap-1">
                              <InputLabel>Email</InputLabel>
                              <InputContainer>
                                <InputText
                                  value={contactEmail}
                                  onChange={(e) =>
                                    setContactEmail(e.target.value)
                                  }
                                />
                              </InputContainer>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <InputLabel>Delivery time slot preference</InputLabel>
                        <p className="text-zinc-500">
                          Add a preferred delivery time slot for Sellar
                          Distribution to aim for if the customer has expressed
                          one.
                        </p>
                        <div className="flex gap-4 rounded-lg bg-zinc-100 p-4">
                          {(
                            [
                              {
                                key: "am",
                                label: "AM",
                              },
                              {
                                key: "pm",
                                label: "Early PM",
                              },
                              {
                                key: "allDay",
                                label: "All day",
                              },
                            ] as const
                          ).map((option) => (
                            <label
                              key={option.key}
                              className={twMerge(
                                "flex w-full cursor-pointer items-center space-x-3 rounded-lg border bg-white px-4 py-3 transition hover:bg-zinc-100",
                                timePreference === option.key
                                  ? "border-zinc-400 bg-zinc-200"
                                  : "border-white",
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={timePreference === option.key}
                                onChange={(e) =>
                                  setTimePreference(
                                    e.target.checked ? option.key : null,
                                  )
                                }
                                className="h-4 w-4 rounded-lg accent-zinc-800 focus:ring-2 focus:ring-zinc-500"
                              />
                              <span className="text-gray-700 capitalize">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {getActionButtons()}
              </>
            ) : null}
            <BasicDialog
              dialogActive={discardOrderDialogActive}
              onDismiss={() => setDiscardOrderDialogActive(false)}
              onDelete={navigateBack}
              deleteButtonText={"Discard"}
              title={`Discard order for ${connection.Retailer.Business.name}?`}
              description={
                "You will lose the order you have created. If you wish to finalise the order later, select 'save as request' to pick back up where you left off."
              }
            />
          </div>
        </OrderOnBehalfOfContext.Provider>
      )}
    </>
  );
};
